import { useState } from 'react';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { DndContext, closestCenter, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AutomationFolder, Automation } from '@/types/automation';

function SortableFolderItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

interface FolderTreeProps {
  folders: AutomationFolder[];
  selectedFolderId?: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: () => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onReorderFolders?: (folders: AutomationFolder[]) => void;
  uncategorizedCount: number;
  automations?: Automation[];
  onEditAutomation?: (automationId: string) => void;
  onDragOver?: (e: React.DragEvent, folderId: string | null) => void;
  onDrop?: (e: React.DragEvent, folderId: string | null) => void;
}

export function FolderTree({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onReorderFolders,
  uncategorizedCount,
  automations = [],
  onEditAutomation,
  onDragOver,
  onDrop
}: FolderTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleDragOver = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) {
      onDragOver(e, folderId);
    }
  };

  const handleDrop = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDrop) {
      onDrop(e, folderId);
    }
  };

  // Sort folders by display_order
  const sortedFolders = [...folders].sort((a, b) => a.display_order - b.display_order);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const sensors = useSensors(pointerSensor);

  const handleFolderDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderFolders) return;

    const oldIndex = sortedFolders.findIndex(f => f.id === active.id);
    const newIndex = sortedFolders.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...sortedFolders];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Update display_order on each
    const updated = reordered.map((f, i) => ({ ...f, display_order: i }));
    onReorderFolders(updated);
  };

  return (
    <div data-testid="folder-tree" className="w-64 border-r bg-muted/10 p-4 flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm">Folders</h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onCreateFolder}
          title="Create Folder"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Automations (root level) */}
      <div
        data-testid="folder-tree-root"
        className={cn(
          "flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-muted",
          selectedFolderId === null && "bg-primary text-primary-foreground"
        )}
        onClick={() => onSelectFolder(null)}
        onDragOver={(e) => handleDragOver(e, null)}
        onDrop={(e) => handleDrop(e, null)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Folder className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-sm truncate font-semibold">Automations</span>
        </div>
        {uncategorizedCount > 0 && (
          <span className="text-xs text-muted-foreground">({uncategorizedCount})</span>
        )}
      </div>

      {/* Folder List */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFolderDragEnd}>
        <SortableContext items={sortedFolders.map(f => f.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-1">
            {sortedFolders.map((folder) => {
              const isExpanded = expandedFolders.has(folder.id);
              const isSelected = selectedFolderId === folder.id;

              return (
                <SortableFolderItem key={folder.id} id={folder.id}>
              {/*
                `group` is required here: the actions button below is styled
                `opacity-0 group-hover:opacity-100`. Without a `group` ancestor that
                variant never fires, so Rename and Delete were permanently invisible
                to a mouse user. `AutomationCardCompact` has always had it.
              */}
              <div
                data-testid="folder-tree-item"
                data-folder-name={folder.name}
                className={cn(
                  "group flex items-center justify-between gap-1 px-2 py-1.5 rounded-md transition-colors hover:bg-muted",
                  isSelected && "bg-primary text-primary-foreground"
                )}
                onDragOver={(e) => handleDragOver(e, folder.id)}
                onDrop={(e) => handleDrop(e, folder.id)}
              >
                {/* Left: Chevron + Folder Icon + Name + Count */}
                <div
                  className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer"
                  onClick={() => onSelectFolder(folder.id)}
                >
                  {/* Chevron (expand/collapse) */}
                  <button
                    type="button"
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} folder ${folder.name}`}
                    aria-expanded={isExpanded}
                    className="flex-shrink-0 p-0.5 hover:bg-accent rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(folder.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                  </button>

                  {/* Folder Icon */}
                  {isExpanded ? (
                    <FolderOpen
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: folder.color || undefined }}
                    />
                  ) : (
                    <Folder
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: folder.color || undefined }}
                    />
                  )}

                  {/* Name */}
                  <span className="text-sm truncate" title={folder.name}>
                    {folder.name}
                  </span>

                  {/* Count */}
                  {(folder.automation_count ?? 0) > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({folder.automation_count})
                    </span>
                  )}
                </div>

                {/* Right: More Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Actions for folder ${folder.name}`}
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRenameFolder(folder.id)}>
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDeleteFolder(folder.id)}
                      className="text-destructive focus:text-destructive"
                      disabled={(folder.automation_count ?? 0) > 0}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Expanded Content - Show Automations in Folder */}
              {/* Sibling of the row above, not a child of it, so it carries its own id. */}
              {isExpanded && (
                <div
                  data-testid="folder-tree-children"
                  data-folder-name={folder.name}
                  className="ml-6 mt-1 space-y-0.5"
                >
                  {automations
                    .filter(a => a.folder_id === folder.id)
                    .map(automation => (
                      <div
                        key={automation.id}
                        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 text-xs text-muted-foreground cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAutomation && automation.id) {
                            onEditAutomation(automation.id);
                          }
                        }}
                        title={automation.name}
                      >
                        <div className="w-1 h-1 rounded-full bg-muted-foreground/50 flex-shrink-0" />
                        <span className="truncate">{automation.name}</span>
                      </div>
                    ))}
                  {automations.filter(a => a.folder_id === folder.id).length === 0 && (
                    <div className="text-xs text-muted-foreground/50 italic px-2 py-1">
                      No automations
                    </div>
                  )}
                </div>
              )}
                </SortableFolderItem>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* Empty State */}
      {sortedFolders.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-xs">No folders yet</p>
          <p className="text-xs">Click + to create one</p>
        </div>
      )}
    </div>
  );
}
